import { translateUi } from "./i18n.ts";
export const JITSI_IFRAME_ALLOW = "autoplay; camera; microphone; display-capture; fullscreen; clipboard-write; speaker-selection";

export type SubstituteMeetingToken = {
  roomName: string;
  displayName: string;
  isModerator: boolean;
  provider: "jitsi";
  jitsiDomain: string;
  title: string;
  url: string;
  studentsMicAllowed: boolean;
  studentsCameraAllowed: boolean;
  chatEnabled: boolean;
  chatOnly: boolean;
};

export function safeJitsiDomain(value: string) {
  const domain = value.trim().toLowerCase();
  return /^[a-z0-9.-]+$/.test(domain) && !domain.startsWith(".") && !domain.endsWith(".") ? domain : "meet.element.io";
}

export function meetingToolbar(meeting: SubstituteMeetingToken) {
  if (meeting.isModerator) return ["microphone", "camera", "desktop", "chat", "participants-pane", "tileview", "raisehand", "settings", "fullscreen", "hangup"];
  const buttons = meeting.chatEnabled || meeting.chatOnly || meeting.studentsMicAllowed || meeting.studentsCameraAllowed
    ? ["raisehand", "hangup"] : ["hangup"];
  if (!meeting.chatOnly && meeting.studentsMicAllowed) buttons.unshift("microphone");
  if (!meeting.chatOnly && meeting.studentsCameraAllowed) buttons.unshift("camera");
  if (meeting.chatEnabled || meeting.chatOnly) buttons.push("chat");
  return [...buttons, "tileview", "fullscreen"];
}

export function meetingConfig(meeting: SubstituteMeetingToken, mediaGranted = false) {
  return {
    startWithAudioMuted: true,
    startWithVideoMuted: true,
    disableInitialGUM: !mediaGranted,
    disableDeepLinking: true,
    prejoinPageEnabled: false,
    prejoinConfig: { enabled: false },
    disableInviteFunctions: true,
    mouseMoveCallbackInterval: 1000,
    disableShortcuts: !meeting.isModerator,
    startAudioOnly: !meeting.isModerator && meeting.chatOnly,
    toolbarButtons: meetingToolbar(meeting),
  };
}

export function directJitsiUrl(meeting: SubstituteMeetingToken, mediaGranted = false) {
  const domain = safeJitsiDomain(meeting.jitsiDomain);
  const url = new URL(`https://${domain}/${encodeURIComponent(meeting.roomName)}`);
  // Jitsi decodes each hash value as JSON, not URLSearchParams form encoding.
  const values = { ...Object.fromEntries(Object.entries(meetingConfig(meeting, mediaGranted)).map(([key, value]) => [`config.${key}`, value])), "userInfo.displayName": meeting.displayName };
  url.hash = Object.entries(values).map(([key, value]) => `${key}=${encodeURIComponent(JSON.stringify(value))}`).join("&");
  return url.toString();
}

export function mediaPermissionMessage(error: unknown) {
  const name = error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return translateUi("Leja për kamerë ose mikrofon u refuzua. Lejoje nga ikona e lejeve pranë adresës dhe provo përsëri.", "Camera or microphone permission was denied. Allow access from the permissions icon beside the address and try again.");
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return translateUi("Nuk u gjet kamerë ose mikrofon i përshtatshëm në këtë pajisje.", "No suitable camera or microphone was found on this device.");
  if (name === "NotReadableError" || name === "TrackStartError") return translateUi("Kamera ose mikrofoni po përdoret nga një program tjetër. Mbylle programin tjetër dhe provo përsëri.", "The camera or microphone is in use by another application. Close it and try again.");
  if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError") return translateUi("Pajisja nuk i mbështet kërkesat e kamerës ose mikrofonit.", "The device does not support the camera or microphone requirements.");
  return translateUi("Kamera ose mikrofoni nuk u hap. Kontrollo lejet e browserit dhe provo përsëri.", "The camera or microphone could not be opened. Check browser permissions and try again.");
}

export function mediaConstraints(token: SubstituteMeetingToken): MediaStreamConstraints {
  return {
    audio: token.isModerator || (!token.chatOnly && token.studentsMicAllowed),
    video: token.isModerator || (!token.chatOnly && token.studentsCameraAllowed),
  };
}

export function needsMedia(token: SubstituteMeetingToken) {
  const constraints = mediaConstraints(token);
  return Boolean(constraints.audio || constraints.video);
}
